import React, { useState } from 'react';
import './RaySelection.css';

const RaySelection = ({ sessionData, onComplete, onBack }) => {
  const [selectedRay, setSelectedRay] = useState(sessionData.raySelection);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Ray names and descriptions in proper Brazilian Portuguese
  const rayData = {
    1: { title: 'PODER', subtitle: 'Poder Pessoal' },
    2: { title: 'SABEDORIA', subtitle: 'Conhecimento' },
    3: { title: 'AMOR', subtitle: 'Relacionamentos' },
    4: { title: 'CRIATIVIDADE', subtitle: 'Expressão' },
    5: { title: 'CURA', subtitle: 'Vitalidade' },
    6: { title: 'PAZ', subtitle: 'Serenidade' },
    7: { title: 'TRANSMUTAÇÃO', subtitle: 'Alquimia' },
    8: { title: 'CLAREZA', subtitle: 'Discernimento' },
    9: { title: 'HARMONIA', subtitle: 'Equilíbrio' },
    10: { title: 'FINANCEIRO', subtitle: 'Abundância' },
    11: { title: 'META', subtitle: 'Propósito' },
    12: { title: 'SAÚDE', subtitle: 'Bem-estar' }
  };

  const handleRayClick = (rayNumber) => {
    if (!isProcessing) {
      setSelectedRay(rayNumber);
    }
  };

  const handleContinue = () => {
    if (selectedRay && !isProcessing) {
      setIsProcessing(true);
      onComplete(selectedRay);
    }
  };

  const handleMouseMove = (e, rayNumber) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;
    
    // Calculate distance and angle for dynamic effects
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const maxDistance = 100;
    const intensity = Math.min(distance / maxDistance, 1);
    
    setMousePos({ x: deltaX, y: deltaY, intensity, rayNumber });
  };

  return (
    <div className="oracle-step ray-selection-step">
      <h2>Escolha seu Raio</h2>
      <p>
        Cada raio representa uma energia primária que influenciará sua consulta ao Oráculo dos 12 Raios.
        Feche os olhos, respire profundamente e deixe sua intuição guiá-lo para o raio que mais ressoa com você neste momento.
      </p>

      <div className="oracle-policy-notice">
        <div className="notice-icon">⚠️</div>
        <p>
          <strong>Política do Oráculo dos 12 Raios:</strong> Uma vez iniciada a consulta, não é possível voltar atrás nas etapas que envolvem revelações místicas. 
          Isso preserva a autenticidade e energia da sua consulta oracular. Escolha conscientemente.
        </p>
      </div>

      <div className="rays-container">
        <div className="rays-grid">
          {Array.from({ length: 12 }, (_, index) => {
            const rayNumber = index + 1;
            return (
              <button
                key={rayNumber}
                className={`ray-option ${selectedRay === rayNumber ? 'selected' : ''}`}
                onClick={() => handleRayClick(rayNumber)}
                onMouseMove={(e) => handleMouseMove(e, rayNumber)}
                onMouseLeave={() => setMousePos({ x: 0, y: 0, intensity: 0, rayNumber: null })}
                data-ray-id={rayNumber}
                style={{
                  transform: mousePos.rayNumber === rayNumber 
                    ? `translate(${mousePos.x * 0.2}px, ${mousePos.y * 0.2}px) scale(${1 + mousePos.intensity * 0.15})`
                    : '',
                  boxShadow: mousePos.rayNumber === rayNumber 
                    ? `0 0 ${30 + mousePos.intensity * 40}px hsl(${(rayNumber - 1) * 30}, 70%, 60%), 0 0 10px rgba(255,255,255,0.5)`
                    : '',
                  background: mousePos.rayNumber === rayNumber 
                    ? `rgba(${255 - mousePos.intensity * 50}, ${255 - mousePos.intensity * 50}, 255, ${0.1 + mousePos.intensity * 0.1})`
                    : ''
                }}
              >
                <div className="ray-visual">
                  <div className="ray-symbol">
                    <img 
                      src={`/assets/images/rays/ray-${rayNumber}.svg`} 
                      alt={`Raio ${rayNumber}`}
                      className="ray-image"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to number if image not found
                        console.warn(`Ray image not found: ray-${rayNumber}.svg`);
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ray-number-fallback" style={{ display: 'none' }}>
                      {rayNumber}
                    </div>
                  </div>
                </div>
                {/* Ray text is now embedded in the image - no separate label needed */}
              </button>
            );
          })}
        </div>
      </div>

      {selectedRay && (
        <div className="selection-confirmation" key={selectedRay}>
          <div className="confirmation-glow"></div>
          <p>Você escolheu o <strong>{rayData[selectedRay].title}</strong></p>
          <small>{rayData[selectedRay].subtitle} • Esta energia primária guiará toda sua consulta ao Oráculo dos 12 Raios</small>
        </div>
      )}

      <div className="step-navigation">
        <button 
          onClick={handleContinue}
          className="continue-btn primary-cta"
          disabled={!selectedRay || isProcessing}
          style={{ margin: '0 auto' }}
        >
          {isProcessing ? 'Processando...' : 'Continuar →'}
        </button>
      </div>
    </div>
  );
};

export default RaySelection;